import React from "react";
import { useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { Video } from "../content/types";
import { getPlayList } from "../../services/ContentServices";
import {
  Box,
  Drawer,
  IconButton,
  InputAdornment,
  TablePagination,
  TextField,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import SearchIcon from "@mui/icons-material/Search";
import { PlayList } from "../content/Playlist";
import { TableBodyPlayList } from "../../utils/ContentCardList";
import {
  AddPlayListTolist,
  RemovePlayListFromList,
} from "../../services/SubscriptionServices";
import { useSnackbar } from "notistack";

interface PlayListDrawerProps {
  open: boolean;
  onClose: () => void;
  storedPlayListIds?: string[];
  selectedSubscription?: string;
  onDataUpdate?: () => void;
}
const PlayListDrawer: React.FC<PlayListDrawerProps> = ({
  open,
  onClose,
  onDataUpdate,
  storedPlayListIds,
  selectedSubscription,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState<boolean>(false);
  const [totalElements, setTotalElements] = useState<number>(0);
  const rowsPerPageOptions = [5, 15, 25];
  const [playlists, setPlaylists] = useState<PlayList[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState<number>(rowsPerPageOptions[0]);
  const [page, setPage] = useState<number>(0);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (open) {
      getPlayListContent();
    }
  }, [page, rowsPerPage, open]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const getPlayListContent = async () => {
    setLoading(true);
    try {
      const response = await getPlayList({ page, size: rowsPerPage });
      setPlaylists(response.data.content);
      setTotalElements(response.data.totalElements);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddToSubscription = async (id: string): Promise<number> => {
    const isPresent =
      storedPlayListIds !== undefined && storedPlayListIds.includes(id);

    const status = isPresent
      ? await RemovePlayListFromList({
          subscriptionId: selectedSubscription as string,
          playlistId: id,
        })
      : await AddPlayListTolist({
          subscriptionId: selectedSubscription as string,
          playlistId: id,
        });

    if (status == 200) {
      enqueueSnackbar(
        isPresent
          ? "Successfully! Playlist has been removed"
          : "Successfully! Playlist has been added",
        {
          variant: "success",
        }
      );

      return 200;
    }

    enqueueSnackbar(
      isPresent
        ? "Failed! Playlist could not be removed"
        : "Failed! Playlist could not be added",
      {
        variant: "error",
      }
    );

    return 401;
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          "& .MuiDrawer-paper": {
            backgroundColor: theme.palette.background.paper,
            borderTopLeftRadius: theme.spacing(2),
            borderBottomLeftRadius: theme.spacing(2),
            width: 500,
            height: "100vh",
            boxSizing: "border-box",
          },
        }}
      >
        <Box
          component="form"
          aria-label="Video List"
          sx={{
            display: "flex",
            backgroundColor: theme.palette.background.paper,
            flexDirection: "column",
            height: "100vh",
            minHeight: "100vh",
            maxHeight: "100vh",
            padding: theme.spacing(1),
            boxSizing: "border-box",
          }}
        >
          {/* conflicts */}
          <Box
            mb={2}
            mt={2}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <IconButton
              onClick={onClose}
              sx={{
                marginRight: theme.spacing(1),
              }}
            >
              <KeyboardBackspaceIcon />
            </IconButton>
            <Box
              sx={{
                width: "75%",
              }}
            >
              <TextField
                placeholder="Search by name"
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              marginBottom: theme.spacing(1),
              scrollbarWidth: "none",
            }}
          >
            <TableBodyPlayList
              drawer
              loading={loading}
              playlists={playlists}
              refreshList={onDataUpdate}
              readOnly={storedPlayListIds}
              callBackchange={handleAddToSubscription}
            />
          </Box>
          <Box sx={{ marginTop: "auto" }}>
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={totalElements}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default PlayListDrawer;
